/**
 * BrightPath - BudgetCard Component
 * BP-04-04: Expenses Frontend
 */

import React from 'react';
import { Edit, Trash2, TrendingUp, TrendingDown } from 'lucide-react';
import type { Budget, BudgetStatus } from '../types';
import { EXPENSE_CATEGORIES } from '../types';
import { formatCurrency, formatBudgetPeriod, cn } from '@/core/lib/utils';

interface BudgetCardProps {
  budget: Budget;
  status?: BudgetStatus;
  onEdit?: (budget: Budget) => void;
  onDelete?: (budget: Budget) => void;
}

export function BudgetCard({ budget, status, onEdit, onDelete }: BudgetCardProps) {
  const categoryConfig = EXPENSE_CATEGORIES[budget.category];
  const spent = status?.spent || 0;
  const percentageUsed = status?.percentageUsed || 0;
  const isOverBudget = status?.isOverBudget || false;

  return (
    <div className="bg-white rounded-xl border border-warm-gray/10 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${categoryConfig.color}15`, color: categoryConfig.color }}
          >
            <span className="text-xl font-bold">{categoryConfig.label[0]}</span>
          </div>
          <div>
            <h3 className="font-semibold text-navy">{categoryConfig.label}</h3>
            <p className="text-sm text-warm-gray">
              {formatBudgetPeriod(budget.year, budget.month)}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-1">
          {onEdit && (
            <button
              onClick={() => onEdit(budget)}
              className="p-2 hover:bg-cream rounded-lg transition-colors"
            >
              <Edit className="w-4 h-4 text-warm-gray" />
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(budget)}
              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4 text-warm-gray hover:text-red-500" />
            </button>
          )}
        </div>
      </div>

      {/* Budget Amount */}
      <div className="mb-4">
        <p className="text-3xl font-bold text-navy">{formatCurrency(budget.monthlyBudget)}</p>
        <p className="text-sm text-warm-gray">Monthly Budget</p>
      </div>

      {/* Progress */}
      {status && (
        <>
          <div className="h-2 bg-cream-dark rounded-full overflow-hidden mb-2">
            <div
              className={cn(
                'h-full rounded-full transition-all duration-500',
                isOverBudget ? 'bg-red-500' : percentageUsed > 75 ? 'bg-amber-500' : 'bg-sage'
              )}
              style={{ width: `${Math.min(percentageUsed, 100)}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-warm-gray">{formatCurrency(spent)} spent</span>
            <span className={cn(
              'font-medium',
              isOverBudget ? 'text-red-500' : 'text-sage'
            )}>
              {percentageUsed.toFixed(0)}%
            </span>
          </div>
        </>
      )}

      {/* Alert Threshold */}
      <div className="mt-4 pt-4 border-t border-warm-gray/10 flex items-center justify-between text-sm">
        <span className="text-warm-gray">Alert at</span>
        <span className="font-medium text-navy">{budget.alertThreshold}%</span>
      </div>
    </div>
  );
}

export default BudgetCard;
